import { useState } from 'react';
import { Loader2, Search, Wrench } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { matchingAPI } from '../../lib/api';
import { toast } from 'sonner';
import { getErrorMessage } from '../../lib/errors';

// BR OS Allocation Backfill (Diagnostics card): corrects group
// payment_match.allocated_amount corruption, Wise or Remittance sourced
// alike (services/bros_allocation_backfill.py, routes/admin_bros_allocation_
// backfill.py). Two recognised signatures: every sibling stamped with the
// PARENT payment's claimed total (all_siblings_claimed_total), or exactly
// one sibling stamped with ITS OWN invoice total instead of the smaller
// partial share it actually received (single_sibling_own_total, added
// 2026-07-27 for the RP-61F50843 follow-up). Preview is read-only and never
// auto-corrects (CLAUDE.md: don't fix what you're diagnosing); Apply writes
// the per-sibling correction to both db.bros_invoices and db.invoices with
// an idempotent old-value guard, safe to re-run. This route shipped without
// a Diagnostics card (dc78584e) -- verified by grepping the whole frontend
// tree for both the endpoint path and any component name, both came back
// empty.
export function BrosAllocationBackfillPanel() {
  const [previewing, setPreviewing] = useState(false);
  const [applying, setApplying] = useState(false);
  const [preview, setPreview] = useState(null);
  const [applyResult, setApplyResult] = useState(null);

  const runPreview = async () => {
    setPreviewing(true);
    setApplyResult(null);
    try {
      const { data } = await matchingAPI.previewBrosAllocationBackfill();
      setPreview(data);
      if (data.correctable_count > 0) {
        toast.warning(`${data.correctable_count} corruptible group(s) found`);
      } else {
        toast.success('No corrupted BR OS allocation groups found');
      }
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setPreviewing(false);
    }
  };

  const runApply = async () => {
    setApplying(true);
    try {
      const { data } = await matchingAPI.applyBrosAllocationBackfill();
      setApplyResult(data);
      toast.success(`${data.applied_count ?? 0} group(s) backfilled`);
      const { data: refreshed } = await matchingAPI.previewBrosAllocationBackfill();
      setPreview(refreshed);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setApplying(false);
    }
  };

  const correctable = preview?.correctable ?? [];
  const uncorrectable = preview?.uncorrectable ?? [];
  const correctableCount = preview?.correctable_count ?? 0;

  // Flatten group -> per-sibling corrections into one row list for display.
  const correctionRows = correctable.flatMap((g) =>
    (g.corrections ?? []).map((c) => ({ ...c, payment_id: g.payment_id, entity_id: g.entity_id, signature: g.signature }))
  );

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2">
          <Wrench className="h-4 w-4 text-amber-500" />
          BR OS Allocation Backfill
        </CardTitle>
        <CardDescription>
          Finds BR OS group payments (Wise or Remittance) with a recognised payment_match.allocated_amount
          corruption signature -- every sibling stamped with the claimed total, or one sibling stamped with
          its own invoice total instead of its true partial share -- and reconciles once the correction is
          substituted in. Apply backfills both db.bros_invoices and db.invoices; a signature match that
          doesn't reconcile is surfaced as uncorrectable, never guessed.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex gap-2">
          <Button data-testid="bros-allocation-backfill-preview" size="sm" variant="outline" onClick={runPreview} disabled={previewing}>
            {previewing ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Search className="h-4 w-4 mr-1" />}
            Preview
          </Button>
          <Button
            data-testid="bros-allocation-backfill-apply"
            size="sm"
            onClick={runApply}
            disabled={applying || !preview || correctableCount === 0}
          >
            {applying ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Wrench className="h-4 w-4 mr-1" />}
            Apply {correctableCount ? `${correctableCount} group(s)` : ''}
          </Button>
        </div>

        {preview && (
          <div className="space-y-2" data-testid="bros-allocation-backfill-result">
            <p className="text-sm text-muted-foreground">{preview.message}</p>
            {correctionRows.length === 0 ? (
              <div className="text-xs text-muted-foreground" data-testid="bros-allocation-backfill-corrections-empty">
                No correctable groups found.
              </div>
            ) : (
              <div className="overflow-x-auto border rounded-lg" data-testid="bros-allocation-backfill-corrections">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b bg-muted/50">
                      <th className="text-left px-3 py-2 font-medium">Payment ID</th>
                      <th className="text-left px-3 py-2 font-medium">BR OS ID</th>
                      <th className="text-left px-3 py-2 font-medium">Invoice ID</th>
                      <th className="text-left px-3 py-2 font-medium">Old Allocated</th>
                      <th className="text-left px-3 py-2 font-medium">New Allocated</th>
                      <th className="text-left px-3 py-2 font-medium">Signature</th>
                    </tr>
                  </thead>
                  <tbody>
                    {correctionRows.map((c) => (
                      <tr key={`${c.payment_id}-${c.bros_id}`} className="border-b last:border-0">
                        <td className="px-3 py-2 font-mono text-xs">{c.payment_id}</td>
                        <td className="px-3 py-2 font-mono text-xs">{c.bros_id}</td>
                        <td className="px-3 py-2 font-mono text-xs">{c.invoice_id}</td>
                        <td className="px-3 py-2 text-xs">{c.old_allocated_amount}</td>
                        <td className="px-3 py-2 text-xs">{c.new_allocated_amount}</td>
                        <td className="px-3 py-2 text-xs">{c.signature}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {uncorrectable.length > 0 && (
              <div className="space-y-1">
                <p className="text-xs font-medium text-red-600 dark:text-red-400">
                  Matches the corruption signature but does NOT reconcile -- needs manual review,
                  not silently dropped.
                </p>
                <div className="overflow-x-auto border rounded-lg border-red-200 dark:border-red-800" data-testid="bros-allocation-backfill-uncorrectable">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b bg-muted/50">
                        <th className="text-left px-3 py-2 font-medium">Payment ID</th>
                        <th className="text-left px-3 py-2 font-medium">Claimed</th>
                        <th className="text-left px-3 py-2 font-medium">Own Total Sum</th>
                        <th className="text-left px-3 py-2 font-medium">Reason</th>
                      </tr>
                    </thead>
                    <tbody>
                      {uncorrectable.map((u) => (
                        <tr key={u.payment_id} className="border-b last:border-0">
                          <td className="px-3 py-2 font-mono text-xs">{u.payment_id}</td>
                          <td className="px-3 py-2 text-xs">{u.claimed_amount}</td>
                          <td className="px-3 py-2 text-xs">{u.own_total_sum ?? 'unreadable'}</td>
                          <td className="px-3 py-2 text-xs">{u.reason ?? 'sum_mismatch'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}

        {applyResult && (
          <div className="space-y-1" data-testid="bros-allocation-backfill-apply-result">
            <p className="text-xs text-muted-foreground">
              Last apply: {applyResult.applied_count ?? 0} group(s) backfilled
              {(applyResult.uncorrectable_count ?? 0) > 0 && `, ${applyResult.uncorrectable_count} uncorrectable`}
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
